type EventCallback = (...args: any[]) => void;

interface Message {
  id: string;
  content: string;
  sender: string;
  timestamp: number;
  status?: 'sending' | 'sent' | 'received' | 'failed';
}

interface CallModalData {
  name: string;
  avatar: string;
  status: string;
  type: 'audio' | 'video';
}

interface IncomingCallData {
  callId: string;
  name: string;
  avatar: string;
  type: 'audio' | 'video';
}

export class UI {
  private events: Map<string, EventCallback[]> = new Map();
  private elements: { [key: string]: HTMLElement | null } = {};
  private ringtoneAudio: HTMLAudioElement | null = null;

  constructor() {
    this.cacheElements();
  }

  init(): void {
    console.log('🎨 Initializing UI...');
    this.bindEvents();
    this.setupMessageInput();
  }

  private cacheElements(): void {
    this.elements = {
      // Sidebar
      sidebar: document.getElementById('sidebar'),
      chatList: document.getElementById('chat-list'),
      newChatBtn: document.getElementById('new-chat-btn'),

      // Chat Window
      chatWindow: document.getElementById('chat-window'),
      emptyState: document.getElementById('empty-state'),
      chatName: document.getElementById('chat-name'),
      chatStatus: document.getElementById('chat-status'),
      chatAvatar: document.getElementById('chat-avatar'),
      messagesContainer: document.getElementById('messages-container'),
      messages: document.getElementById('messages'),
      messageInput: document.getElementById('message-input') as HTMLInputElement,
      sendBtn: document.getElementById('send-btn'),

      // Call Buttons
      voiceCallBtn: document.getElementById('voice-call-btn'),
      videoCallBtn: document.getElementById('video-call-btn'),

      // Call Modal
      callModal: document.getElementById('call-modal'),
      callName: document.getElementById('call-name'),
      callStatus: document.getElementById('call-status'),
      callAvatar: document.getElementById('call-avatar'),
      callVideoContainer: document.getElementById('call-video-container'),
      localVideo: document.getElementById('local-video') as HTMLVideoElement,
      remoteVideo: document.getElementById('remote-video') as HTMLVideoElement,
      endCallBtn: document.getElementById('end-call-btn'),
      muteBtn: document.getElementById('mute-btn'),
      videoToggleBtn: document.getElementById('video-toggle-btn'),

      // Incoming Call
      incomingCall: document.getElementById('incoming-call'),
      incomingName: document.getElementById('incoming-name'),
      incomingAvatar: document.getElementById('incoming-avatar'),
      answerIncomingBtn: document.getElementById('answer-incoming-btn'),
      declineIncomingBtn: document.getElementById('decline-incoming-btn'),

      // Toast
      toastContainer: document.getElementById('toast-container'),

      // Audio
      ringtone: document.getElementById('ringtone') as HTMLAudioElement
    };

    this.ringtoneAudio = this.elements.ringtone as HTMLAudioElement;
  }

  private bindEvents(): void {
    // New chat button
    this.elements.newChatBtn?.addEventListener('click', () => {
      this.emit('newChat');
    });

    // Send message
    this.elements.sendBtn?.addEventListener('click', () => {
      this.handleSendMessage();
    });

    // Call buttons
    this.elements.voiceCallBtn?.addEventListener('click', () => {
      const chatId = this.getCurrentChatId();
      if (chatId) this.emit('voiceCall', chatId);
    });

    this.elements.videoCallBtn?.addEventListener('click', () => {
      const chatId = this.getCurrentChatId();
      if (chatId) this.emit('videoCall', chatId);
    });

    // Call controls
    this.elements.endCallBtn?.addEventListener('click', () => {
      this.emit('endCall');
    });

    this.elements.muteBtn?.addEventListener('click', () => {
      this.toggleMute();
    });

    this.elements.videoToggleBtn?.addEventListener('click', () => {
      this.toggleVideo();
    });

    // Incoming call
    this.elements.answerIncomingBtn?.addEventListener('click', () => {
      const callId = this.elements.incomingCall?.dataset.callId;
      if (callId) this.emit('answerCall', callId);
    });

    this.elements.declineIncomingBtn?.addEventListener('click', () => {
      const callId = this.elements.incomingCall?.dataset.callId;
      if (callId) this.emit('declineCall', callId);
    });
  }

  private setupMessageInput(): void {
    this.elements.messageInput?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleSendMessage();
      }
    });
  }

  private handleSendMessage(): void {
    const input = this.elements.messageInput as HTMLInputElement;
    const message = input?.value.trim();

    if (message) {
      this.emit('sendMessage', message);
      input.value = '';
    }
  }

  addMessage(message: Message): void {
    const messagesEl = this.elements.messages;
    if (!messagesEl) return;

    const messageEl = document.createElement('div');
    messageEl.className = `message ${message.sender === 'me' ? 'message-sent' : 'message-received'}`;
    messageEl.dataset.id = message.id;

    const time = new Date(message.timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });

    messageEl.innerHTML = `
      <div class="message-content">
        <p>${this.escapeHtml(message.content)}</p>
        <span class="message-time">${time}</span>
        ${message.status ? `<span class="message-status">${this.getStatusIcon(message.status)}</span>` : ''}
      </div>
    `;

    messagesEl.appendChild(messageEl);
    this.scrollToBottom();
  }

  private getStatusIcon(status: string): string {
    const icons: { [key: string]: string } = {
      sending: '⏳',
      sent: '✓',
      received: '✓✓',
      failed: '✗'
    };
    return icons[status] || '';
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private scrollToBottom(): void {
    const container = this.elements.messagesContainer;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }

  showChatWindow(chatId: string, name: string, avatar: string, status: string): void {
    this.elements.chatWindow?.classList.remove('d-none');
    this.elements.emptyState?.classList.add('d-none');
    
    if (this.elements.chatName) this.elements.chatName.textContent = name;
    if (this.elements.chatStatus) this.elements.chatStatus.textContent = status;
    if (this.elements.chatAvatar) {
      (this.elements.chatAvatar as HTMLImageElement).src = avatar;
    }

    // Clear messages
    if (this.elements.messages) {
      this.elements.messages.innerHTML = '';
    }

    if (this.elements.chatWindow) {
      this.elements.chatWindow.dataset.chatId = chatId;
    }
  }

  private getCurrentChatId(): string | null {
    return this.elements.chatWindow?.dataset.chatId ?? null;
  }

  updateChatPreview(chatId: string, lastMessage: string, timestamp: number, username?: string): void {
    // Update or create chat item in the list
    let chatItem = this.elements.chatList?.querySelector(`[data-chat-id="${chatId}"]`) as HTMLElement;
    
    if (!chatItem) {
      chatItem = this.createChatItem(chatId, username || chatId, 'default-avatar.svg', lastMessage, timestamp);
      this.elements.chatList?.prepend(chatItem);
    } else {
      // Update name if provided
      if (username) {
        const nameEl = chatItem.querySelector('.chat-name');
        if (nameEl) nameEl.textContent = username;
      }
      
      const preview = chatItem.querySelector('.chat-preview');
      const time = chatItem.querySelector('.chat-time');
      
      if (preview) preview.textContent = lastMessage;
      if (time) time.textContent = this.formatTime(timestamp);
      
      // Move to top
      this.elements.chatList?.prepend(chatItem);
    }
  }

  private createChatItem(chatId: string, name: string, avatar: string, preview: string, timestamp: number): HTMLElement {
    const item = document.createElement('div');
    item.className = 'chat-item d-flex gap-3';
    item.dataset.chatId = chatId;

    item.innerHTML = `
      <img class="rounded-circle" src="${avatar}" alt="${name}" width="48" height="48">
      <div class="flex-grow-1 min-w-0">
        <div class="d-flex justify-content-between mb-1">
          <h6 class="mb-0 fw-semibold">${this.escapeHtml(name)}</h6>
          <small class="text-muted">${this.formatTime(timestamp)}</small>
        </div>
        <p class="mb-0 text-muted small text-truncate">${this.escapeHtml(preview)}</p>
      </div>
    `;

    item.addEventListener('click', () => {
      this.emit('selectChat', chatId);
      this.showChatWindow(chatId, name, avatar, 'online');
    });

    return item;
  }

  private formatTime(timestamp: number): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  }

  showCallModal(data: CallModalData): void {
    const modal = new (window as any).bootstrap.Modal(this.elements.callModal);
    modal.show();
    
    if (this.elements.callName) this.elements.callName.textContent = data.name;
    if (this.elements.callStatus) this.elements.callStatus.textContent = data.status;
    if (this.elements.callAvatar) {
      (this.elements.callAvatar as HTMLImageElement).src = data.avatar;
    }

    if (data.type === 'video') {
      this.elements.callVideoContainer?.classList.remove('d-none');
      this.elements.videoToggleBtn?.classList.remove('d-none');
    } else {
      this.elements.callVideoContainer?.classList.add('d-none');
      this.elements.videoToggleBtn?.classList.add('d-none');
    }
  }

  hideCallModal(): void {
    const modal = (window as any).bootstrap.Modal.getInstance(this.elements.callModal);
    if (modal) modal.hide();
    this.stopLocalStream();
  }

  updateCallStatus(status: string): void {
    if (this.elements.callStatus) {
      this.elements.callStatus.textContent = status;
    }
  }

  setLocalStream(stream: MediaStream): void {
    if (this.elements.localVideo) {
      this.elements.localVideo.srcObject = stream;
    }
  }

  setRemoteStream(stream: MediaStream): void {
    if (this.elements.remoteVideo) {
      this.elements.remoteVideo.srcObject = stream;
    }
  }

  private stopLocalStream(): void {
    if (this.elements.localVideo) {
      const stream = this.elements.localVideo.srcObject as MediaStream;
      stream?.getTracks().forEach(track => track.stop());
      this.elements.localVideo.srcObject = null;
    }
    if (this.elements.remoteVideo) {
      this.elements.remoteVideo.srcObject = null;
    }
  }

  private toggleMute(): void {
    const video = this.elements.localVideo;
    if (video?.srcObject) {
      const stream = video.srcObject as MediaStream;
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        this.elements.muteBtn?.classList.toggle('active', !audioTrack.enabled);
      }
    }
  }

  private toggleVideo(): void {
    const video = this.elements.localVideo;
    if (video?.srcObject) {
      const stream = video.srcObject as MediaStream;
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        this.elements.videoToggleBtn?.classList.toggle('active', !videoTrack.enabled);
      }
    }
  }

  showIncomingCall(data: IncomingCallData): void {
    this.elements.incomingCall?.classList.remove('d-none');
    
    if (this.elements.incomingName) {
      this.elements.incomingName.textContent = data.name;
    }
    if (this.elements.incomingAvatar) {
      (this.elements.incomingAvatar as HTMLImageElement).src = data.avatar;
    }
    if (this.elements.incomingCall) {
      this.elements.incomingCall.dataset.callId = data.callId;
    }
  }

  hideIncomingCall(): void {
    this.elements.incomingCall?.classList.add('d-none');
  }

  playRingtone(): void {
    if (this.ringtoneAudio) {
      this.ringtoneAudio.play().catch(err => {
        console.warn('Could not play ringtone:', err);
      });
    }
  }

  stopRingtone(): void {
    if (this.ringtoneAudio) {
      this.ringtoneAudio.pause();
      this.ringtoneAudio.currentTime = 0;
    }
  }

  showToast(message: string, type: 'success' | 'error' | 'info' = 'info'): void {
    const bgClass = type === 'success' ? 'bg-success' : type === 'error' ? 'bg-danger' : 'bg-primary';
    const toastEl = document.createElement('div');
    toastEl.className = 'toast align-items-center text-white border-0 ' + bgClass;
    toastEl.setAttribute('role', 'alert');
    toastEl.innerHTML = `
      <div class="d-flex">
        <div class="toast-body">${message}</div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
      </div>
    `;

    this.elements.toastContainer?.appendChild(toastEl);
    const toast = new (window as any).bootstrap.Toast(toastEl);
    toast.show();
    
    toastEl.addEventListener('hidden.bs.toast', () => {
      toastEl.remove();
    });
  }

  // Event emitter
  on(event: string, callback: EventCallback): void {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event)?.push(callback);
  }

  private emit(event: string, ...args: any[]): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      callbacks.forEach(callback => callback(...args));
    }
  }
}
